import axios from "./helpers/axios";
import axiosInstance from "../../utils/axios";
import { BitrixFile, Deal } from "./types/deal";
import { getContactById, getContactsByIds } from "./contact";
import { DealProduct } from "./types/product";
import { sleep } from "./helpers/timer";
import { getCompaniesByIds, getCompanyById } from "./company";
import { Status } from "./types/status";
import { QueryFilter } from "./helpers/apiHelper";
import { InvoiceStages } from "./types/invoice";
import { DEAL_BUSINESS_PARNERUS, DEAL_CATEGORY_ID } from "./accountConfig";

export const getDealProducts = (dealId: string): Promise<DealProduct[]> => {
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.productrows.get?id=${dealId}`
      )
      .then((result) => {
        resolve(result.data.result);
      })
      .catch((error) =>
        reject({
          message: `There was an error finding the products: ${
            error.response?.data?.error_description || error.message
          }`,
        })
      );
  });
};

export const getDeal = async (
  dealId: string,
  dealStages: Status[],
  invoiceStatuses: Status[]
): Promise<Deal> => {
  const deal = (
    await axios.get(
      `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.get?ID=${dealId}`
    )
  ).data.result;

  return <Deal>{
    ...deal,
    STAGE: dealStages.find((s) => s.STATUS_ID === deal.STAGE_ID),
    CONTACT: await getContactById(deal.CONTACT_ID),
    COMPANY:
      deal.COMPANY_ID &&
      deal.COMPANY_ID !== "0" &&
      (await getCompanyById(deal.COMPANY_ID)),
    INVOICE_STATUS:
      deal.UF_CRM_1787078147 &&
      invoiceStatuses.find((s) => s.STATUS_ID === deal.UF_CRM_1787078147),
  };
};

export const getDealsForDeskTable = async (
  dealStages: Status[],
  invoiceStatuses: Status[],
  accountId: string,
  filtersAdded?: QueryFilter[]
): Promise<Deal[]> => {
  let next = 0;
  let dealsList: Deal[] = [];

  let filters = "";
  if (filtersAdded) {
    for (const filter of filtersAdded) {
      filters += `&FILTER[${filter.field}]=${filter.value}`;
    }
  }

  do {
    const queryResult = await axios.get(
      `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.list?SELECT[0]=*&SELECT[1]=UF_*&FILTER[UF_CRM_PARNERUS_ACCOUNT_ID]=${accountId}${filters}&ORDER[ID]=DESC&start=${next}`
    );
    next = queryResult.data.next;
    dealsList = dealsList.concat(queryResult.data.result);
    await sleep(1500);
  } while (next);

  const contacts = await getContactsByIds(
    dealsList.reduce(
      (prev, deal) => (deal.CONTACT_ID ? [...prev, deal.CONTACT_ID] : prev),
      <string[]>[]
    ),
    accountId
  );
  const companies = await getCompaniesByIds(
    dealsList.reduce(
      (prev, deal) =>
        deal.COMPANY_ID && deal.COMPANY_ID !== "0"
          ? [...prev, deal.COMPANY_ID]
          : prev,
      <string[]>[]
    ),
    accountId
  );

  return dealsList.map((deal: Deal) => {
    return <Deal>{
      ...deal,
      STAGE: dealStages.find((s) => s.STATUS_ID === deal.STAGE_ID),
      CONTACT: contacts.find((c) => c.ID === deal.CONTACT_ID),
      COMPANY: companies.find((c) => c.ID === deal.COMPANY_ID),
      INVOICE_STATUS:
        deal.UF_CRM_1787078147 &&
        invoiceStatuses.find((s) => s.STATUS_ID === deal.UF_CRM_1787078147),
    };
  });
};

export const getDealsForDashboard = async (
  accountId: string
): Promise<Deal[]> => {
  let next = 0;
  let dealsList: Deal[] = [];

  do {
    const queryResult = await axios.get(
      `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.list?SELECT[0]=*&SELECT[1]=UF_*&FILTER[UF_CRM_PARNERUS_ACCOUNT_ID]=${accountId}&ORDER[ID]=DESC&start=${next}`
    );
    next = queryResult.data.next;
    dealsList = dealsList.concat(queryResult.data.result);
    await sleep(1500);
  } while (next);

  return dealsList;
};

export const createDeal = (
  deal: Partial<Deal>,
  files: BitrixFile[],
  accountId: string,
  mediadorId: number,
  invoiceId?: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.add`, {
        FIELDS: {
          CATEGORY_ID: DEAL_CATEGORY_ID,
          ...deal,
          UF_CRM_PARNERUS_ACCOUNT_ID: accountId,
          UF_CRM_1787078534: files,
          UF_CRM_1787078653: invoiceId,
          UF_CRM_69CE81A5C12D4: mediadorId,
          UF_CRM_1787078147: InvoiceStages.New,
          UF_CRM_69CE81A6124B2: DEAL_BUSINESS_PARNERUS,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const updateDealInvoiceStatus = (dealId: string) => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.update`, {
        ID: dealId,
        FIELDS: {
          UF_CRM_1787078147: InvoiceStages.Paid,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const updateDealDocuments = async (
  dealId: string,
  files: BitrixFile[]
): Promise<string> => {
  // Se delega al backend a proposito. Bitrix no admite append en un campo de
  // archivos multiple, asi que hay que bajar los que ya estan y reenviarlos junto
  // con los nuevos; y el nombre original viaja en content-disposition, que Bitrix
  // no expone por CORS. Desde el navegador los archivos se conservarian pero
  // renombrados a "archivo-<id>".
  const response = await axiosInstance.post("/api/order/append-deal-documents", {
    dealId,
    files,
  });

  return response.data?.results;
};

export const setDealProducts = (
  dealId: string,
  productRows: Partial<DealProduct>[]
): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.deal.productrows.set`,
        {
          id: dealId,
          rows: productRows,
        }
      )
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};
